import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy, Check, ArrowRight, Terminal, HeartPulse, ShoppingCart,
  Bot, Target, Brain, Shield, HelpCircle, MessageSquare, Wallet
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsMessagingCommands() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const commandCategories = [
    {
      name: 'Status',
      icon: HeartPulse,
      color: 'text-[#3EF1C7]',
      commands: [
        {
          cmd: '/pulse',
          description: 'Quick health check of your store. Returns CI Score, trend direction, active alerts, and agent status.',
          syntax: '/pulse',
          example: '/pulse',
          response: `CI Score: 847/1000 (A) ▲ +12
Trend: Improving over 7 days
Active Alerts: 2 (1 low inventory, 1 deal expiring)
Agents: 3 active, 0 pending approval
Revenue (24h): $4,231.00 (+8.2% vs yesterday)`
        },
        {
          cmd: '/brief',
          description: 'Full daily summary with revenue, orders, agent activity, health score changes, and recommendations.',
          syntax: '/brief [date]',
          example: '/brief',
          response: `Daily Brief — March 17, 2026

Revenue: $12,847.32 (+15.3% vs prev day)
Orders: 47 (3 exceptions flagged)
Avg Order Value: $273.35

Health Score: 847 → 859 (+12)
  Fulfillment: 91/100
  Communication: 88/100
  Consistency: 85/100

Agent Decisions (24h):
  PriceGuard: 3 approved, 1 pending
  StockSense: 2 approved
  DealPilot: 1 auto-approved

Recommendations:
  Restock SKU WDG-SM-BLU (12 units remaining)
  Deal "Spring Sale" expires in 2 days`
        },
        {
          cmd: '/inbox',
          description: 'Shows pending items needing your attention — agent approvals, expiring deals, flagged exceptions.',
          syntax: '/inbox',
          example: '/inbox',
          response: `Inbox — 4 items pending

1. [APPROVAL] PriceGuard wants to lower SKU-445 by 12%
   Confidence: 0.82 | Tier: Standard
   Reply: approve 1 / reject 1

2. [ALERT] Low inventory: Widget Pro (8 units left)
   Reorder point: 20 | Lead time: 5 days

3. [DEAL] "Flash Friday" expires in 6 hours
   Performance: 234 clicks, 18 conversions

4. [EXCEPTION] Order #4521 delivery delay flagged
   Carrier: USPS | Expected: Mar 15 | Status: In Transit`
        }
      ]
    },
    {
      name: 'Commerce',
      icon: ShoppingCart,
      color: 'text-sky-400',
      commands: [
        {
          cmd: '/deals',
          description: 'List, create, and manage pricing deals. Without arguments, shows active deals.',
          syntax: '/deals [list|create|pause|resume|delete] [options]',
          example: '/deals list',
          response: `Active Deals (3)

1. Spring Sale — 20% off Electronics
   Status: Active | Ends: Mar 25
   Clicks: 1,247 | Conversions: 89 | Revenue: $6,231

2. Bundle Discount — Buy 2 Get 10% off
   Status: Active | Ends: Apr 1
   Clicks: 456 | Conversions: 34 | Revenue: $2,108

3. Clearance — 40% off Winter Collection
   Status: Active | Ends: Mar 20
   Clicks: 892 | Conversions: 67 | Revenue: $3,450`
        },
        {
          cmd: '/margins',
          description: 'Margin analysis for products or categories. Shows cost, revenue, and profit margins.',
          syntax: '/margins [product|category] [identifier]',
          example: '/margins category Electronics',
          response: `Margin Analysis — Electronics

Products: 24 | Avg Margin: 42.3%
Top Margin: Premium Headphones (58.2%)
Low Margin: USB-C Cable (12.1%)

Revenue (30d): $28,450
COGS (30d): $16,428
Gross Profit: $12,022

Warning: 3 products below 15% margin threshold`
        },
        {
          cmd: '/inventory',
          description: 'Stock level overview across all locations. Highlights low-stock and out-of-stock items.',
          syntax: '/inventory [product|low|oos]',
          example: '/inventory low',
          response: `Low Stock Items (5)

1. Widget Pro (WDG-SM-BLU) — 8 units
   Reorder point: 20 | All locations
2. Premium Case (CS-PRO-BLK) — 14 units
   Reorder point: 25 | East Warehouse
3. Screen Protector (SP-ULT-CLR) — 3 units
   Reorder point: 50 | All locations
4. Charging Dock (CD-DUO-WHT) — 11 units
   Reorder point: 15 | West Warehouse
5. Travel Adapter (TA-UNI-BLK) — 6 units
   Reorder point: 30 | All locations`
        },
        {
          cmd: '/top',
          description: 'Top-performing SKUs ranked by revenue or units sold.',
          syntax: '/top [revenue|units] [limit]',
          example: '/top revenue 5',
          response: `Top SKUs by Revenue (5)

1. Premium Headphones (WH-1000XM4) — $12,450 | 62 units
2. Widget Pro Bundle (WDG-BND-01) — $8,920 | 148 units
3. Smart Watch (SW-GEN5-BLK) — $7,340 | 41 units
4. Organic Skincare Set (SKIN-01) — $5,210 | 58 units
5. Travel Adapter (TA-UNI-BLK) — $3,980 | 220 units`
        },
        {
          cmd: '/valuation',
          description: 'Total inventory valuation at landed cost (qty × landed unit cost) with a per-component breakdown. Set landed cost via "set cost for <SKU>" or by giving Brahma a supplier shipment with freight & duties to allocate. Estimates cost where none is on file.',
          syntax: '/valuation',
          example: '/valuation',
          response: `Inventory Valuation (at landed cost)

Total: $184,320.00   Retail: $402,510.00
SKUs counted: 142 (124 with cost on file, 18 estimated)

Landed cost components:
  Unit cost:   $148,900.00
  Freight:     $21,640.00
  Duties:      $9,310.00
  Insurance:   $2,470.00
  Handling:    $1,500.00
  Other:       $500.00

18 SKUs used an estimated cost (price × 0.4) — set landed cost for exact valuation.`
        },
        {
          cmd: '/revenue',
          description: 'Revenue trend over the last N days with a per-day breakdown.',
          syntax: '/revenue [days]',
          example: '/revenue 7',
          response: `Revenue — Last 7 Days

Total: $89,432.18
  Mon $11,230  Tue $13,450  Wed $12,890
  Thu $14,120  Fri $15,670  Sat $12,340
  Sun $9,732`
        },
        {
          cmd: '/aov',
          description: 'Average order value over a window.',
          syntax: '/aov [days]',
          example: '/aov 30',
          response: `Average Order Value — Last 30 Days

AOV: $273.35
Orders: 412
Revenue: $112,620.20`
        },
        {
          cmd: '/customers',
          description: 'Customer breakdown — new vs returning and your top customers by spend.',
          syntax: '/customers [days]',
          example: '/customers 90',
          response: `Customers — Last 90 Days

Total: 318 (new 241 | returning 77)
Guest orders: 54

Top customers:
  1. alex@example.com — 6 orders, $2,140
  2. sam@example.com — 4 orders, $1,690
  3. jordan@example.com — 3 orders, $1,205`
        },
        {
          cmd: '/returns',
          description: 'Return rate and the most common return reasons.',
          syntax: '/returns [days]',
          example: '/returns 30',
          response: `Returns — Last 30 Days

Return rate: 4.2% (17 returns / 405 orders)

Top reasons:
  1. Wrong size — 7
  2. Changed mind — 4
  3. Damaged in transit — 3
  4. Not as described — 2
  5. unspecified — 1`
        }
      ]
    },
    {
      name: 'Finance',
      icon: Wallet,
      color: 'text-green-400',
      commands: [
        {
          cmd: '/cashflow',
          description: 'Your platform balance — available, pending, and reserved funds — plus pending payouts.',
          syntax: '/cashflow',
          example: '/cashflow',
          response: `Cashflow

Available: $8,240.50
Pending:   $1,920.00
Reserved:  $500.00 (escrow holds)
Currency:  USD

Pending payouts:
  $3,000.00 — scheduled Mar 20`
        },
        {
          cmd: '/fees',
          description: 'Platform fees you have paid (settlement, escrow, M2M) with a breakdown.',
          syntax: '/fees [days]',
          example: '/fees 30',
          response: `Platform Fees — Last 30 Days

Total: $612.40
  settlement_platform: $420.10
  escrow_release:      $92.30
  m2m_facilitation:    $100.00`
        }
      ]
    },
    {
      name: 'Agents',
      icon: Bot,
      color: 'text-amber-400',
      commands: [
        {
          cmd: '/agents',
          description: 'List all configured agents with status, trust tier, and recent activity.',
          syntax: '/agents [list|status]',
          example: '/agents',
          response: `Active Agents (3)

1. PriceGuard — Pricing optimization
   Tier: Standard | Mode: Hybrid | Confidence: 0.85
   Decisions (7d): 12 approved, 2 rejected, 1 pending

2. StockSense — Inventory management
   Tier: Trusted | Mode: Auto | Confidence: 0.92
   Decisions (7d): 8 auto-approved

3. DealPilot — Deal optimization
   Tier: Standard | Mode: Manual | Confidence: 0.78
   Decisions (7d): 5 approved, 3 pending`
        },
        {
          cmd: '/agentmode',
          description: 'Set the approval mode for an agent: auto (no approval needed), manual (all need approval), or hybrid (auto above threshold).',
          syntax: '/agentmode <agent> <auto|manual|hybrid>',
          example: '/agentmode PriceGuard hybrid',
          response: `PriceGuard approval mode updated to: hybrid
Actions above 0.85 confidence will auto-approve.
Actions below 0.85 will require manual approval.`
        },
        {
          cmd: '/mintier',
          description: 'Set the minimum trust tier required for an agent to execute actions.',
          syntax: '/mintier <agent> <new|standard|trusted|verified>',
          example: '/mintier StockSense trusted',
          response: `StockSense minimum trust tier set to: trusted
Agent currently at tier: trusted
Agent can continue operating.`
        },
        {
          cmd: '/threshold',
          description: 'Set the confidence threshold for hybrid-mode auto-approval.',
          syntax: '/threshold <agent> <0.0-1.0>',
          example: '/threshold DealPilot 0.90',
          response: `DealPilot confidence threshold updated: 0.78 -> 0.90
In hybrid mode, only actions with >=90% confidence
will auto-approve. Others require manual review.`
        }
      ]
    },
    {
      name: 'Goals',
      icon: Target,
      color: 'text-emerald-400',
      commands: [
        {
          cmd: '/goals',
          description: 'View active goals and progress tracking.',
          syntax: '/goals',
          example: '/goals',
          response: `Active Goals (2)

1. Q1 Revenue Target
   Target: $150,000 | Current: $127,340 (84.9%)
   Deadline: Mar 31 | Pace: On track

2. Reduce Return Rate
   Target: <5% | Current: 6.2%
   Deadline: Apr 15 | Pace: Needs attention`
        },
        {
          cmd: '/goal set',
          description: 'Create a new goal with a target metric and deadline.',
          syntax: '/goal set <metric> <target> <deadline>',
          example: '/goal set avg_order_value $300 2026-04-01',
          response: `Goal created: Increase Average Order Value

Metric: avg_order_value
Target: $300.00
Current: $273.35
Deadline: April 1, 2026
Agents will factor this goal into recommendations.`
        }
      ]
    },
    {
      name: 'Intelligence',
      icon: Brain,
      color: 'text-purple-400',
      commands: [
        {
          cmd: '/report',
          description: 'Generate an on-demand report for a specific time range or topic.',
          syntax: '/report <type> [time_range]',
          example: '/report revenue 7d',
          response: `Revenue Report — Last 7 Days

Total Revenue: $89,432.18
vs Previous 7d: +$12,340 (+16.0%)

By Day:
  Mon: $11,230  Tue: $13,450  Wed: $12,890
  Thu: $14,120  Fri: $15,670  Sat: $12,340
  Sun: $9,732

Top Products by Revenue:
  1. Premium Headphones: $12,450
  2. Widget Pro Bundle: $8,920
  3. Smart Watch: $7,340

Full report: app.commerceindex.com/reports/rev-7d-abc`
        },
        {
          cmd: '/digest',
          description: 'Agent activity digest showing all decisions, rationale, and outcomes.',
          syntax: '/digest [agent] [time_range]',
          example: '/digest PriceGuard 24h',
          response: `PriceGuard Digest — Last 24 Hours

Decisions: 4 total
  Auto-approved: 2 | Manually approved: 1 | Pending: 1

Actions Taken:
  1. Lowered SKU-112 price by 8% (confidence: 0.91)
     Reason: Competitor price drop detected
     Result: 23% increase in conversion rate

  2. Raised SKU-445 price by 5% (confidence: 0.88)
     Reason: Low inventory + high demand
     Result: Margin improvement +$2.40/unit

  3. Bundle suggestion for SKU-112 + SKU-789
     Confidence: 0.85 | Status: Approved

  4. Discount extension for "Flash Friday" deal
     Confidence: 0.72 | Status: Pending approval`
        }
      ]
    },
    {
      name: 'Admin',
      icon: Shield,
      color: 'text-rose-400',
      commands: [
        {
          cmd: '/policy',
          description: 'View or set operational policies that govern agent behavior, notifications, and thresholds.',
          syntax: '/policy [view|set <key> <value>]',
          example: '/policy view',
          response: `Active Policies

brief_time: 08:00
brief_channels: telegram,slack
quiet_hours: 22:00-07:00
max_discount: 30%
min_margin: 15%
auto_reorder: true
reorder_lead_days: 5
escalation_threshold: critical`
        },
        {
          cmd: '/recall',
          description: 'Semantic search across your CI memory — past decisions, conversations, agent rationale.',
          syntax: '/recall <search query>',
          example: '/recall why did we discount headphones last month',
          response: `Found 3 relevant memories:

1. [Feb 14] PriceGuard lowered Premium Headphones by 15%
   Reason: Valentine's Day competitor pricing pressure
   Approved by: @alex | Result: +340 units sold

2. [Feb 20] Deal "Presidents Day Audio" created
   Discount: 20% on all audio products
   Duration: Feb 17-20 | Revenue: $8,920

3. [Mar 1] Policy update: max_discount set to 30%
   Changed by: @sarah via /policy set max_discount 30%`
        },
        {
          cmd: '/threads',
          description: 'View active cross-platform conversation threads. Conversations sync across all connected channels.',
          syntax: '/threads [list|view <id>]',
          example: '/threads list',
          response: `Active Threads (3)

1. #thread-4521 — Order exception discussion
   Platforms: Telegram, Slack
   Last message: 2 hours ago

2. #thread-4518 — Deal approval for Spring Sale
   Platforms: WhatsApp, Slack
   Last message: 5 hours ago

3. #thread-4515 — Inventory reorder discussion
   Platforms: Telegram
   Last message: 1 day ago`
        }
      ]
    },
    {
      name: 'System',
      icon: HelpCircle,
      color: 'text-[#94A3B8]',
      commands: [
        {
          cmd: '/help',
          description: 'Show the full command list with brief descriptions.',
          syntax: '/help [command]',
          example: '/help',
          response: `Commerce Index Commands

Status: /pulse /brief /inbox
Commerce: /deals /margins /inventory /top /valuation /revenue /aov /customers /returns
Finance: /cashflow /fees
Agents: /agents /agentmode /mintier /threshold
Goals: /goals /goal set
Intel: /report /digest
Admin: /policy /recall /threads
System: /help /link /llm

Type /help <command> for details on any command.`
        },
        {
          cmd: '/link',
          description: 'Link your messaging account to your CI merchant account via OTP verification.',
          syntax: '/link <domain>',
          example: '/link mystore.com',
          response: `Account linking initiated for mystore.com

A 6-digit verification code has been sent to
the admin email on file.

Please enter the code here within 10 minutes.`
        },
        {
          cmd: '/llm',
          description: 'Check the health and status of the LLM provider powering CI agents.',
          syntax: '/llm',
          example: '/llm',
          response: `LLM Status: Operational

Provider: Anthropic (Claude)
Latency: 1.2s avg (last 100 requests)
Token Usage (today): 45,230 / 100,000
Error Rate: 0.1%

Fallback Provider: OpenAI (GPT-4o)
Status: Standby`
        }
      ]
    }
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Command Reference
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Messaging Commands
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Complete reference for all Commerce Index commands available across Telegram, WhatsApp,
          Slack, and iMessage. Commands work identically on every platform.
        </p>
      </div>

      {/* Quick Reference */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Quick Reference
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Category</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Commands</th>
                </tr>
              </thead>
              <tbody>
                {commandCategories.map((cat, i) => (
                  <tr key={cat.name} className={i < commandCategories.length - 1 ? 'border-b border-[#22304A]/50' : ''}>
                    <td className="p-4">
                      <span className={`font-medium ${cat.color}`}>{cat.name}</span>
                    </td>
                    <td className="p-4 text-[#94A3B8]">
                      {cat.commands.map(c => (
                        <code key={c.cmd} className="text-[#E2E8F0] bg-[#0B1120] px-2 py-0.5 rounded text-xs mr-2 mb-1 inline-block">
                          {c.cmd}
                        </code>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Command Categories */}
      {commandCategories.map((category) => {
        const Icon = category.icon;
        return (
          <section key={category.name} className="mb-12">
            <h2 className="text-2xl font-semibold text-white mb-6">
              <span className="flex items-center gap-3">
                <Icon className={`w-6 h-6 ${category.color}`} />
                {category.name} Commands
              </span>
            </h2>
            <div className="space-y-6">
              {category.commands.map((command) => (
                <Card key={command.cmd} className="bg-[#0D1628] border-[#22304A]">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <code className="text-lg font-bold text-[#3EF1C7]">{command.cmd}</code>
                    </div>
                    <p className="text-[#94A3B8] text-sm mb-4">{command.description}</p>

                    <div className="mb-3">
                      <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Syntax</p>
                      <code className="text-sm text-[#E2E8F0] bg-[#0B1120] px-3 py-1 rounded border border-[#22304A]">
                        {command.syntax}
                      </code>
                    </div>

                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-wider mb-2">Example</p>
                      <div className="relative">
                        <button
                          onClick={() => copyToClipboard(`${command.example}\n\n${command.response}`, `cmd-${command.cmd}`)}
                          className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
                        >
                          {copiedCode === `cmd-${command.cmd}` ? (
                            <Check className="w-4 h-4 text-[#3EF1C7]" />
                          ) : (
                            <Copy className="w-4 h-4 text-[#64748B]" />
                          )}
                        </button>
                        <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`> ${command.example}\n\n${command.response}`}</code></pre>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        );
      })}

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/account-linking">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Account Linking <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Connect your messaging identity to your CI account</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/daily-briefs">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Daily Briefs <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Set up automated daily summaries and alerts</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
